import React, { useState, useMemo } from 'react';
import { Team } from './SortableEntity';
import { RankedPlayer } from '../hooks/useTeamManagement';
import { Player, PlayerWithLeagueContext, LeagueOutput } from '../generated-api';
import { PlayerRatingInfo } from '../hooks/useRecentPlayers';
import PlayerAvatar from './common/PlayerAvatar';
import SearchButton from './common/SearchButton';
import FilterButton from './common/FilterButton';
import CompactRatingDisplay from './common/CompactRatingDisplay';
import { resolveLeagueIcon } from '../utils/leagueIcon';

interface PlayerSelectionGridProps {
  players: (Player | PlayerWithLeagueContext)[];
  selectedPlayerIds: string[];
  rankedPlayers: RankedPlayer[];
  teams: Team[];
  onTogglePlayerSelection: (playerId: string) => void;
  onCreateTeam: (playerId: string) => void;
  onAddPlayerToTeam: (playerId: string, teamId: string) => void;
  onRemovePlayerFromTeam: (playerId: string) => void;
  getPlayerTeam: (playerId: string) => Team | undefined;
  getTeamColor: (teamId: string) => any;
  headerAction?: React.ReactNode;
  isTeamEditMode?: boolean;
  playersSelectedForTeam?: string[];
  leagues?: LeagueOutput[];
  recentPlayerIds?: string[];
  /** Map of player ID to their last play date (ISO string) */
  lastPlayedMap?: Record<string, string>;
  /** Map of player ID to their global league rating info */
  playerRatingsMap?: Record<string, PlayerRatingInfo>;
}

/**
 * Format a date as a relative time string (e.g., "2d ago", "1w ago")
 */
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1d ago';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
};

const PlayerSelectionGrid: React.FC<PlayerSelectionGridProps> = ({
  players,
  selectedPlayerIds,
  rankedPlayers,
  teams,
  onTogglePlayerSelection,
  onCreateTeam,
  onAddPlayerToTeam,
  onRemovePlayerFromTeam,
  getPlayerTeam,
  getTeamColor,
  headerAction,
  isTeamEditMode = false,
  playersSelectedForTeam = [],
  leagues = [],
  recentPlayerIds = [],
  lastPlayedMap = {},
  playerRatingsMap = {}
}) => {
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [selectedLeagueFilter, setSelectedLeagueFilter] = useState<string>('');
  const [showRecentOnly, setShowRecentOnly] = useState(false);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  // Build a map of player IDs to their leagues
  const playerLeaguesMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    leagues.forEach(league => {
      league.playerIds?.forEach(playerId => {
        if (!map[playerId]) {
          map[playerId] = [];
        }
        map[playerId].push(league.id);
      });
    });
    return map;
  }, [leagues]);

  // Filter players based on search and filters
  const filteredPlayers = useMemo(() => {
    return players.filter(player => {
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        if (!player.name.toLowerCase().includes(query)) {
          return false;
        }
      }

      // League filter
      if (selectedLeagueFilter) {
        const playerLeagues = playerLeaguesMap[player.id] || [];
        if (!playerLeagues.includes(selectedLeagueFilter)) {
          return false;
        }
      }

      // Recent players filter
      if (showRecentOnly && recentPlayerIds.length > 0) {
        if (!recentPlayerIds.includes(player.id)) {
          return false;
        }
      }

      // Selected only filter
      if (showSelectedOnly) {
        if (!selectedPlayerIds.includes(player.id)) {
          return false;
        }
      }

      return true;
    });
  }, [players, searchQuery, selectedLeagueFilter, playerLeaguesMap, showRecentOnly, recentPlayerIds, showSelectedOnly, selectedPlayerIds]);

  const hasActiveFilters = !!(selectedLeagueFilter || showRecentOnly || showSelectedOnly);

  const handleClearFilters = () => {
    setSelectedLeagueFilter('');
    setShowRecentOnly(false);
    setShowSelectedOnly(false);
  };

  // Get all leagues for filter dropdown (excluding Unranked and game-specific leagues)
  const filterableLeagues = leagues.filter(l => l.name !== 'Unranked' && !l.isGameSpecificLeague);

  return (
    <div className="bg-surface shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-medium text-heading">Select Players</h2>
          <span className="text-sm text-muted">
            ({filteredPlayers.length}{filteredPlayers.length !== players.length ? ` of ${players.length}` : ''})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <SearchButton
            isExpanded={searchExpanded}
            onToggle={() => setSearchExpanded(!searchExpanded)}
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search players..."
          />
          <FilterButton
            onClick={() => setFilterExpanded(!filterExpanded)}
            isExpanded={filterExpanded}
            hasActiveFilters={hasActiveFilters}
            showOnAllScreens={true}
          />
          {headerAction && <div className="hidden sm:block">{headerAction}</div>}
        </div>
      </div>

      {/* Mobile header action */}
      {headerAction && (
        <div className="sm:hidden mb-4">
          {headerAction}
        </div>
      )}

      {/* Filter Panel */}
      {filterExpanded && (
        <div className="mb-4 p-4 bg-surface-base rounded-lg border border-edge">
          <div className="flex flex-wrap gap-4">
            {/* League Filter */}
            {filterableLeagues.length > 0 && (
              <div className="flex-1 min-w-[150px]">
                <label htmlFor="league-filter" className="block text-sm font-medium text-body mb-1">
                  League
                </label>
                <select
                  id="league-filter"
                  value={selectedLeagueFilter}
                  onChange={(e) => setSelectedLeagueFilter(e.target.value)}
                  className="w-full border border-edge-input rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All Leagues</option>
                  {filterableLeagues.map(league => (
                    <option key={league.id} value={league.id}>
                      {resolveLeagueIcon(league.icon, '')} {league.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Quick Filters */}
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-body">Quick Filters</span>
              <div className="flex flex-wrap gap-2">
                {recentPlayerIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowRecentOnly(!showRecentOnly)}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      showRecentOnly
                        ? 'bg-primary-100 text-primary-800 border border-primary-300'
                        : 'bg-surface text-body border border-edge-input hover:bg-surface-hover'
                    }`}
                  >
                    🕐 Recent ({recentPlayerIds.length})
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowSelectedOnly(!showSelectedOnly)}
                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    showSelectedOnly
                      ? 'bg-primary-100 text-primary-800 border border-primary-300'
                      : 'bg-surface text-body border border-edge-input hover:bg-surface-hover'
                  }`}
                >
                  ✓ Selected ({selectedPlayerIds.length})
                </button>
              </div>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 pt-3 border-t border-edge">
              <button
                type="button"
                onClick={handleClearFilters}
                className="text-sm text-primary-700 hover:text-primary-800"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}

      {filteredPlayers.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted">
            {searchQuery || hasActiveFilters
              ? 'No players match your search or filters.'
              : 'No players available.'}
          </p>
          {(searchQuery || hasActiveFilters) && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                handleClearFilters();
                setSearchExpanded(false);
                setFilterExpanded(false);
              }}
              className="mt-2 text-sm text-primary-700 hover:text-primary-800"
            >
              Clear search and filters
            </button>
          )}
        </div>
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {filteredPlayers.map((player) => {
          const isSelected = selectedPlayerIds.includes(player.id);
          const isSelectedForTeam = isTeamEditMode && playersSelectedForTeam.includes(player.id);
          return (
            <div
              data-track="player_select_card"
              key={player.id}
              role="button"
              tabIndex={0}
              aria-label={`Toggle selection for ${player.name}`}
              className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                isTeamEditMode && isSelectedForTeam
                  ? 'border-2 border-primary-500 bg-primary-100 text-primary-900'
                  : isSelected
                  ? 'border-primary-500 bg-primary-50 text-primary-900'
                  : 'border-edge bg-surface text-body hover:bg-surface-hover'
              }`}
              onClick={() => onTogglePlayerSelection(player.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onTogglePlayerSelection(player.id);
                }
              }}
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 relative">
                  <PlayerAvatar name={player.name} imageUrl={player.imageUrl} size="medium" />
                  {isSelected && (
                    <div className="absolute -top-0.5 -right-0.5 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center border-2 border-white">
                      <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="font-medium truncate">{player.name}</div>
                    {/* Rating badge - from playerRatingsMap or leagueRating */}
                    {(() => {
                      const ratingInfo = playerRatingsMap[player.id] ||
                        ('leagueRating' in player && player.leagueRating ? {
                          rating: player.leagueRating.rating,
                          sigma: player.leagueRating.sigma,
                          isRatingUncertain: player.leagueRating.isRatingUncertain,
                          gamesPlayed: player.leagueRating.gamesPlayed
                        } : null);
                      return ratingInfo ? (
                        <CompactRatingDisplay
                          rating={ratingInfo.rating}
                          sigma={ratingInfo.sigma}
                          isUncertain={ratingInfo.isRatingUncertain}
                          className="flex-shrink-0 ml-2 text-xs"
                        />
                      ) : null;
                    })()}
                  </div>
                  {/* Context info row */}
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-muted">
                    {/* Last played */}
                    {lastPlayedMap[player.id] && (
                      <span className="flex items-center">
                        <span className="text-green-700 mr-0.5">●</span>
                        {formatRelativeTime(lastPlayedMap[player.id])}
                      </span>
                    )}
                    {/* Games played - from playerRatingsMap or leagueRating */}
                    {(() => {
                      const ratingInfo = playerRatingsMap[player.id] ||
                        ('leagueRating' in player && player.leagueRating ? player.leagueRating : null);
                      return ratingInfo ? (
                        <span>
                          {ratingInfo.gamesPlayed} game{ratingInfo.gamesPlayed !== 1 ? 's' : ''}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  {/* Team badge */}
                  {(() => {
                    const team = getPlayerTeam(player.id);
                    return team ? (
                      <div className="text-xs mt-1">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          getTeamColor(team.id)?.bg || 'bg-surface-subtle'
                        } ${getTeamColor(team.id)?.text || 'text-gray-800'}`}>
                          {team.name}
                        </span>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      <p className={`text-xs mt-4 ${
        selectedPlayerIds.length < 2
          ? 'text-warning-700'
          : 'text-muted'
      }`}>
        {selectedPlayerIds.length < 2
          ? `Select ${2 - selectedPlayerIds.length} more player${selectedPlayerIds.length === 1 ? '' : 's'} to continue`
          : `Selected: ${selectedPlayerIds.length} players`
        }
      </p>
    </div>
  );
};

export default PlayerSelectionGrid;